<template>
  <v-form ref="projectForm" lazy-validation>
    <v-row align="center" class="mb-0">
      <v-col cols="12" sm="7" md="4" xl="2">
        <v-select
          v-model="selectedCandidateId"
          :items="candidateOptions"
          item-text="label"
          item-value="value"
          label="Candidate"
          outlined
          dense
          hide-details
          style="margin-bottom: 0.75em; margin-top: 0.5em;"
        />
      </v-col>
      <v-col cols="12" sm="5" md="4" xl="2">
        <v-chip
          v-if="assignmentAnticipatedRevenue"
          color="primary"
          style="padding: 1.5em 1em; width: 100%;"
        >
          Anticipated Revenue: {{ assignmentAnticipatedRevenue | currency }}
        </v-chip>
        <v-chip
          v-if="selectedCandidateEndAt && new Date(selectedCandidateEndAt) <= Date.now() && assignmentAnticipatedRevenue === 0"
          color="primary"
          outlined
          small
          style="padding: 1.8em 1em; width: 100%;"
        >
          Cannot calculate anticipated<br>revenue of finalized contracts
        </v-chip>
      </v-col>
    </v-row>
    <DealContract
      v-if="selectedCandidate"
      ref="dealContractForm"
      :candidateId="selectedCandidate.id"
      :readonly="readonly"
      :workedHoursAverageDefault="workedHoursAverageDefault"
      :overtimeThresholdDefault="overtimeThresholdDefault"
      :overtimeRateDefault="overtimeRateDefault"
      @setJobTitle="setJobTitle"
      @setWorkedHoursAverage="setWorkedHoursAverage"
      @setPayFrequencyId="setPayFrequencyId"
      @setContractTypeId="setContractTypeId"
      @setLengthOfContract="setLengthOfContract"
      @setConversionFee="setConversionFee"
      @setStartAt="setStartAt"
      @setEndAt="setEndAt"
      @setEmployeeTypeId="setEmployeeTypeId"
      @setCorporateName="setCorporateName"
      @setEin="setEin"
      @setPayRate="setPayRate"
      @setPayRateTypeId="setPayRateTypeId"
      @setBillRate="setBillRate"
      @setBillRateTypeId="setBillRateTypeId"
      @setOvertimeEligible="setOvertimeEligible"
      @setOvertimeThreshold="setOvertimeThreshold"
      @setOvertimeRate="setOvertimeRate"
      @setTimeSheetType="setTimeSheetType"
      @setClockIPAddress="setClockIPAddress"
      @setCommissions="setCommissions"
      @setExpenses="setExpenses"
      @setContractorCommissionEnabled="setContractorCommissionEnabled"
      @setContractorCommissionMarkup="setContractorCommissionMarkup"
    />
  </v-form>
</template>

<script>
import DealContract from './DealContract.vue'
import { mapActions } from 'vuex'
import { CONTRACT_TO_HIRE } from '@/lib/constants/contractTypeDefault'

export default {
  props: [
    'readonly',
    'candidates',
    'workedHoursAverageDefault',
    'overtimeThresholdDefault',
    'overtimeRateDefault'
  ],
  components: {
    DealContract
  },
  data () {
    return {
      selectedCandidateId: null,
      CONTRACT_TO_HIRE,
      assignmentAnticipatedRevenue: 0
    }
  },
  computed: {
    sortedCandidates () {
      const candidates = [...(this.candidates || [])]

      candidates.sort((a, b) => {
        const aLabel = this.getCandidateLabel(a).toLowerCase()
        const bLabel = this.getCandidateLabel(b).toLowerCase()
        return aLabel.localeCompare(bLabel)
      })

      return candidates
    },
    candidateOptions () {
      return this.sortedCandidates.map((candidate) => {
        return {
          label: this.getCandidateLabel(candidate),
          value: candidate.id
        }
      })
    },
    selectedCandidate () {
      if (!this.candidates || this.candidates.length === 0) {
        return null
      }

      const candidate = this.candidates.find(item => item.id === this.selectedCandidateId)
      return candidate || this.sortedCandidates[0]
    },
    isLastCandidate () {
      if (!this.sortedCandidates || this.sortedCandidates.length === 0) return true
      const currentCandidate = this.selectedCandidate
      if (!currentCandidate) return true
      const currentIndex = this.sortedCandidates.findIndex(c => c.id === currentCandidate.id)
      return currentIndex === this.sortedCandidates.length - 1
    },
    selectedCandidateEndAt () {
      return this.selectedCandidate
        ? this.selectedCandidate.dealAssignment.endAt
        : null
    },
    anticipatedRevenueInputs () {
      const a = this.selectedCandidate && this.selectedCandidate.dealAssignment
      return JSON.stringify({
        billRateTypeId: a ? a.billRateTypeId : null,
        billRate: a ? a.billRate : null,
        workedHoursAverage: a ? a.workedHoursAverage : null,
        endAt: a ? a.endAt : null
      })
    }
  },
  watch: {
    candidates: {
      immediate: true,
      handler (value) {
        if (!value || value.length === 0) {
          this.selectedCandidateId = null
          return
        }

        const selectedCandidateExists = value.some(candidate => candidate.id === this.selectedCandidateId)
        if (!selectedCandidateExists) {
          this.selectedCandidateId = this.getAlphabeticallyFirstCandidateId(value)
        }
      }
    },
    anticipatedRevenueInputs: {
      immediate: true,
      handler: async function (serialized) {
        const inputs = JSON.parse(serialized)
        if (inputs.billRateTypeId && inputs.billRate && inputs.workedHoursAverage > 0 && inputs.endAt) {
          if (new Date(inputs.endAt) > Date.now()) {
            this.assignmentAnticipatedRevenue = await this.getAnticipatedRevenue({
              billRateTypeId: inputs.billRateTypeId,
              billRate: inputs.billRate,
              workedHoursAverage: inputs.workedHoursAverage,
              endDate: inputs.endAt
            })
          } else {
            this.assignmentAnticipatedRevenue = 0
          }
        } else {
          this.assignmentAnticipatedRevenue = 0
        }
      }
    }
  },
  methods: {
    ...mapActions('projects', ['getAnticipatedRevenue']),
    hasValue (value) {
      return value !== null && value !== undefined && value !== ''
    },
    isCandidateAssignmentValid (candidate) {
      const assignment = candidate && candidate.dealAssignment

      if (!assignment) {
        return false
      }

      const hasRequiredFields = [
        assignment.jobTitle,
        assignment.workedHoursAverage,
        assignment.payFrequencyId,
        assignment.contractTypeId,
        assignment.startAt,
        assignment.timeSheetType,
        assignment.payRate,
        assignment.payRateTypeId,
        assignment.billRate,
        assignment.billRateTypeId
      ].every(this.hasValue)

      if (!hasRequiredFields) {
        return false
      }

      if (assignment.contractTypeId === this.CONTRACT_TO_HIRE && !this.hasValue(assignment.endAt)) {
        return false
      }

      if (assignment.overtimeEligible && (!this.hasValue(assignment.overtimeThreshold) || !this.hasValue(assignment.overtimeRate))) {
        return false
      }

      return true
    },
    getCandidateLabel (candidate) {
      const firstName = candidate && candidate.firstName ? candidate.firstName : ''
      const lastName = candidate && candidate.lastName ? candidate.lastName : ''
      const fullName = `${firstName} ${lastName}`.trim()

      return fullName || 'Candidate - Missing Name'
    },
    getAlphabeticallyFirstCandidateId (candidates) {
      return [...candidates]
        .sort((a, b) => {
          const aLabel = this.getCandidateLabel(a).toLowerCase()
          const bLabel = this.getCandidateLabel(b).toLowerCase()
          return aLabel.localeCompare(bLabel)
        })[0].id
    },
    setJobTitle (value, id) {
      this.$emit('setJobTitle', value, id)
    },
    setWorkedHoursAverage (value, id) {
      this.$emit('setWorkedHoursAverage', value, id)
    },
    setPayFrequencyId (value, id) {
      this.$emit('setPayFrequencyId', value, id)
    },
    setContractTypeId (value, id) {
      this.$emit('setContractTypeId', value, id)
    },
    setLengthOfContract (value, id) {
      this.$emit('setLengthOfContract', value, id)
    },
    setConversionFee (value, id) {
      this.$emit('setConversionFee', value, id)
    },
    setStartAt (value, id) {
      this.$emit('setStartAt', value, id)
    },
    setEndAt (value, id) {
      this.$emit('setEndAt', value, id)
    },
    setEmployeeTypeId (value, id) {
      this.$emit('setEmployeeTypeId', value, id)
    },
    setCorporateName (value, id) {
      this.$emit('setCorporateName', value, id)
    },
    setEin (value, id) {
      this.$emit('setEin', value, id)
    },
    setPayRate (value, id) {
      this.$emit('setPayRate', value, id)
    },
    setPayRateTypeId (value, id) {
      this.$emit('setPayRateTypeId', value, id)
    },
    setBillRate (value, id) {
      this.$emit('setBillRate', value, id)
    },
    setBillRateTypeId (value, id) {
      this.$emit('setBillRateTypeId', value, id)
    },
    setOvertimeEligible (value, id) {
      this.$emit('setOvertimeEligible', value, id)
    },
    setOvertimeThreshold (value, id) {
      this.$emit('setOvertimeThreshold', value, id)
    },
    setOvertimeRate (value, id) {
      this.$emit('setOvertimeRate', value, id)
    },
    setTimeSheetType (value, id) {
      this.$emit('setTimeSheetType', value, id)
    },
    setClockIPAddress (value, id) {
      this.$emit('setClockIPAddress', value, id)
    },
    setCommissions (value, id) {
      this.$emit('setCommissions', value, id)
    },
    setExpenses (value, id) {
      this.$emit('setExpenses', value, id)
    },
    setContractorCommissionEnabled (value, id) {
      this.$emit('setContractorCommissionEnabled', value, id)
    },
    setContractorCommissionMarkup (value, id) {
      this.$emit('setContractorCommissionMarkup', value, id)
    },
    isProjectValid () {
      const errorMessage = 'Please fill out the required fields to proceed'

      // Validate parent form
      const isParentFormValid = this.$refs.projectForm
        ? this.$refs.projectForm.validate()
        : true
      if (!isParentFormValid) {
        return errorMessage
      }

      // Validate the currently displayed candidate's contract form
      const isVisibleContractFormValid = this.$refs.dealContractForm && typeof this.$refs.dealContractForm.isContractValid === 'function'
        ? this.$refs.dealContractForm.isContractValid() === true
        : true
      if (!isVisibleContractFormValid) {
        return errorMessage
      }

      // If there are more candidates to fill out, navigate to the next one
      if (!this.isLastCandidate) {
        const currentIndex = this.sortedCandidates.findIndex(c => c.id === this.selectedCandidate.id)
        this.selectedCandidateId = this.sortedCandidates[currentIndex + 1].id
        return null
      }

      // On the last candidate — validate all candidates to catch any that may have been left incomplete
      const firstInvalidCandidate = this.sortedCandidates.find(candidate => !this.isCandidateAssignmentValid(candidate))
      if (firstInvalidCandidate) {
        this.selectedCandidateId = firstInvalidCandidate.id
        return `Please fill out the required fields to proceed for ${this.getCandidateLabel(firstInvalidCandidate)}`
      }

      return true
    },
    goToPreviousCandidate () {
      const currentCandidate = this.selectedCandidate
      if (!currentCandidate) return false

      // If the user is already on the first candidate, return false so parent component
      // navigates to previous step instead of previous candidate.
      const currentIndex = this.sortedCandidates.findIndex(c => c.id === currentCandidate.id)
      if (currentIndex <= 0) return false

      // Navigate to the previous candidate
      this.selectedCandidateId = this.sortedCandidates[currentIndex - 1].id

      // True means that it is possible to navigate to previous candidate
      return true
    }
  }
}
</script>
